<script setup lang="ts">
type EnumData = App.Base.Data.EnumData;

import type { HybridlyFormData } from '@/@types/global';

import { ButtonColor, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Form from '@/components/form/Form.vue';
import FormFooter from '@/components/formFooter/FormFooter.vue';
import FormHeader from '@/components/formHeader/FormHeader.vue';
import StudentScholarshipFormFields from './StudentScholarshipFormFields.vue';

const props = defineProps<{
  isEditing?: boolean;
  form: HybridlyFormData<{
    type: EnumData;
    institute_id: number;
    scholarship_id: number;
    amount: number;
    years_renewable: number;
    status: EnumData;
  }>;
  returnUrl?: string;
  statuses: EnumData[];
  types: EnumData[];
  student: App.Students.Data.StudentData;
}>();

const isCancelDialogOpen = ref(false);

const { onBackForward } = useBackForward();

// TODO: Fix intecepting the back button
onBackForward(() => {
  router.abort();
});

function handleToggleCancelDialog() {
  if (props.form.isDirty) {
    isCancelDialogOpen.value = !isCancelDialogOpen.value;
    return;
  }
  handleRedirect();
}

function handleRedirect() {
  router.navigate({
    url:
      props.returnUrl || route('students.student-scholarships.index', { student: props.student.id })
  });
}
</script>

<template>
  <Form @submit.prevent="form.submit">
    <template #header>
      <FormHeader
        canClose
        :title="`${isEditing ? 'Edit' : 'Add'} a Scholarship Application`"
        :description="
          isEditing
            ? 'Use the form below to edit the scholarship application.'
            : 'Fill out the form below add a new scholarship application.'
        "
        @onClose="handleToggleCancelDialog"
      />
    </template>
    <div class="divide-y-slate-200 flex flex-col gap-y-8">
      <StudentScholarshipFormFields :form="form" :types="types" :statuses="statuses" />
    </div>
    <template #footer>
      <FormFooter>
        <Button
          type="submit"
          :color="ButtonColor.primary"
          :isDisabled="form.processing || !form.isDirty"
          :isLoading="form.processing"
          :variant="ButtonVariant.solid"
          >Confirm</Button
        >
        <Button
          :color="ButtonColor.slate"
          :variant="ButtonVariant.outlined"
          type="button"
          @click="handleToggleCancelDialog"
        >
          Back to scholarship applications
        </Button>
      </FormFooter>
    </template>
  </Form>
  <DangerDialog
    :title="`Cancel ${isEditing ? 'editing' : 'adding a new'} scholarship application`"
    :message="`Are you sure you want to cancel ${
      isEditing ? 'editing' : 'adding'
    } this scholarship application? All ${isEditing ? 'changes' : 'information'} will be lost.`"
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Go back to applications page"
    @onCancel="handleToggleCancelDialog"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleRedirect"
  />
</template>
